import {Event} from '../lib/constants/Event'

/**
 * User interface manipulation and handling
 *
 * @class
 * @alias api.ui
 */
class Ui {
    /** @hideconstructor */
    constructor(api) {
        this.api = api
    }

    /**
     * Show a notification
     *
     * @param {string} name - The plugin ID of the plugin that sent the notification
     * @param {string} title - Notification title
     * @param {string} message - Notification message
     * @param {boolean} sticky - Optional, default false, if true notification stays till user closes it
     */
    showNotification(name, title, message, sticky) {
        this.api.events.trigger(name, Event.NOTIFICATION_ADD, {
            plugin: name,
            title: title,
            message: message,
            sticky: sticky || false
        });
    }

    // addSidebarTab(name) {
    //     console.log("This", this.api.configurator.addSidebarTab({id: 'tester', name: 'hello'}));
    // }

    /**
     * Display a dialog with the specified content.
     *
     * Supported dialog actions:
     * <dl>
     *     <dt>`dialog:enablePrimaryBtn`</dt>
     *      <dd>Enables the primary button</dd>
     *     <td>`dialog:disablePrimaryBtn`</dt>
     *      <dd>Disables the primary button
     * </dl>
     * @see {@link Component.DialogComponent}
     *
     * @example
     * import {api} from 'writer'
     *
     * const tertiary = [{
     *      caption: this.context.i18n.t('Remove'),
     *      callback: () => {
     *          // Do something
     *      }
     * }]
     *
     * api.ui.showDialog(
     *   require('./XimimageSoftcropComponent'),
     *   {
     *       src: img.src,
     *       width: this.props.node.width,
     *       height: this.props.node.height,
     *       crops: this.props.node.crops,
     *       callback: this.setSoftcropData.bind(this)
     *   },
     *   {
     *       tertiary: tertiary
     *   }
     * )
     *
     * @param {*} contentComponent A substance component or tool that will be rendered inside the dialog
     * @param {Object} props A object that will be passed as props to contentComponent
     *
     * @param {Component.DialogComponent.Options} options Options passed to dialog
     * @param {string} [options.title] Dialog title
     * @param {(string|boolean)} [options.primary] Primary button caption, default is i18n representation of Ok, set to false to disable button
     * @param {(string|boolean)} [options.secondary] Secondary button caption, default is i18n representation of Cancel, set to false to disable button
     * @param {(string|Object)} [options.tertiary] Third button
     * @param {boolean} [options.center] As default the dialog is centered over the editor area, set to true to center over the full writer
     */
    showDialog(contentComponent, props, options) {
        this.api.modal.showDialog(contentComponent, props, options);
    }

    /**
     * Display a number of messages and different options depending on the
     * severity on each message. Messages can be of type info, warning or error.
     *
     * If there are error messages the user will not be able to continue.
     * If there are warnings the user can continue but suggested to cancel.
     * If only info messages only a continue is possible.
     *
     * @example
     * import {api} from 'writer'
     *
     * api.ui.showMessageDialog(
     *    validationResponses,
     *    function () {
     *      // The user wants to contine
     *    }.bind(this),
     *    function () {
     *      // The user wants to cancel
     *    }.bind(this)
     * )
     *
     * @param {array} messages An array of message objects. Each object have the properties type (string: info, warning, error), plugin (string: the plugin name) and message (string)
     * @param {Function} cbContinue Callback function for when the user press continue
     * @param {Function} cbCancel Callback function for when the user press cancel
     * @param {string} [heading] Additional text to display above the messages
     */
    showMessageDialog(messages, cbContinue, cbCancel, heading) {
        var level = 0; // 0 = info, 1 = warning, 2 = error
        for (var n = 0; n < messages.length; n++) {
            if (level < 1 && messages[n].type === 'warning') {
                // Show both continue and cancel buttons if exists
                level = 1;
            }
            else if (level < 2 && messages[n].type === 'error') {
                // Only show cancel button
                level = 2;
                break;
            }
        }

        var props = {
                level: level
            },
            options = {
                global: true,
                title: this.api.getLabel('message'),
                heading: heading,
                primary: false,
                secondary: false
            };


        const cancelLabel = this.api.getLabel('cancel')
        const continueLabel = this.api.getLabel('continue')
        if (level === 2) {
            options.primary = cancelLabel
            props.cbPrimary = cbCancel;
        }
        else if (level === 1) {
            options.primary = continueLabel
            props.cbPrimary = cbContinue;

            options.secondary = cancelLabel
            props.cbSecondary = cbCancel;
        }
        else {
            options.primary = continueLabel
            props.cbPrimary = cbContinue;

            if (cbCancel) {
                options.secondary = cancelLabel
                props.cbSecondary = cbCancel;
            }
        }

        this.api.modal.showMessageDialog(
            messages,
            props,
            options
        );
    }

    /**
     * Display a simple confirmation dialog
     *
     * @param {string} title dialog heading
     * @param {string} message dialog message
     * @param {object} buttons object with primary/secondary objects for button specifics
     *
     * @example
     *
     * api.ui.showConfirmDialog(
     *      'Title-string',
     *      'Message string',
     *      {
     *          primary: {
     *              label: this.getLabel('Ok'),
     *              callback: () => { logic }
     *          },
     *          secondary: {
     *              label: this.getLabel('Cancel'),
     *              callback: () => { logic }
     *          }
     *      }
     * )
     */
    showConfirmDialog(title, message, buttons) {
        const messages = [{ type: 'info', message }]
        const props = {
            cbPrimary: buttons.primary.callback || null,
            cbSecondary: buttons.secondary.callback || null
        }
        const options = {
            title,
            primary: buttons.primary.label || this.getLabel('save'),
            secondary: buttons.secondary.label || this.getLabel('cancel'),
        }

        this.api.modal.showMessageDialog(messages, props, options)
    }

    /**
     * Deprecated, use `import {UIComponent} from 'writer'` statements for UIComponents instead
     *
     * @deprecated 2018-10-15
     */
    getComponent(name) {

        const {configurator} = this.api

        const actualName = configurator.deprecatedComponentsMap[name] || name
        const component = configurator.getComponent(name)

        console.warn(`api.ui.getComponent('${actualName}') is deprecated, use import {${actualName}} from 'writer' instead`)

        return component
    }

}
export {Ui}
